import React, { FC, useContext, useState } from 'react';
import { Box, Collapse, List, ListItem, Typography } from '@mui/material';
import { i18n } from '../../i18n/i18n';
import { SECTION } from '../../constants/constants';
import CustomActions from '../CustomActions/CustomActions';
import { ReactComponent as BreachedSiteIcon } from '../../images/breached-site.svg';
import { ReactComponent as ArrowDownIcon } from '../../images/arrow-down.svg';
import { ReactComponent as ArrowUpIcon } from '../../images/arrow-up.svg';
import { ReactComponent as PossibleMatchesIcon } from '../../images/possible-matches.svg';
import { ReactComponent as SearchedItemsIcon } from '../../images/searched-items.svg';
import { BreachedObject, Tag } from '../../models/common-types';
import { COLORS, Tooltip } from '@cellebrite/saas-ui-common';
import IconButton from '@mui/material/IconButton';
import { AddTagIcon } from '@cellebrite/saas-ui-icons';
import Popup from '../Popup/Popup';
import TagsManager from '../TagsManager/TagsManager';
import TagsViewer from '../TagsViewer/TagsViewer';
import { GlobalContext } from '../../contexts/globalContext';
import MarkedText from '../MarkedText/MarkedText';
import { Element } from 'react-scroll';
import MultiMarkedText from '../MultiMarkedText/MultiMarkedText';
import { multiMarking } from '../../utils/utils';

interface CustomInfoCardRowProps {
    item: {
        keywords?: string[];
        tags: Array<Tag>;
        id: string;
        value: string;
        sources?: any;
        actions?: any;
        itemType?: string;
        type?: string;
        additionalInfo?: {
            primaryInfoText?: string;
            secondaryInfoText?: string;
            associatedSites?: BreachedObject[];
        };
    };
}

const CustomInfoCardRow: FC<CustomInfoCardRowProps> = ({ item }) => {
    const { reportData } = useContext(GlobalContext);
    const [showActions, setShowActions] = useState(false);
    const [actionsOpen, setActionsOpen] = useState(false);
    const [showTag, setShowTag] = useState(false);
    const [tagOpen, setTagOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const getObject = (type) => {
        const obj = {};
        obj[`& mark:nth-of-type(${reportData.currentFindLink?.index + 1})`] =
            item.id === reportData.currentFindLink?.id &&
            type === reportData.currentFindLink?.type &&
            reportData.currentFindLink?.section === SECTION.PERSONAL_INFO
                ? { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` }
                : null;
        return obj;
    };

    return (
        <>
            <Box
                sx={{
                    'display': 'flex',
                    'justifyContent': 'space-between',
                    columnGap: 1,
                    'borderRadius': '8px',
                    'p': '8px',
                    '&:hover': { backgroundColor: COLORS.MUTED_HOVER },
                    'backgroundColor': actionsOpen || tagOpen ? COLORS.MUTED_HOVER : null,
                }}
                onMouseEnter={() => {
                    setShowTag(true);
                    setShowActions(true);
                }}
                onMouseLeave={() => {
                    setShowTag(false);
                    setShowActions(false);
                }}
                onClick={(event) => (event['stopParentCollapsePropagation'] = true)}
            >
                <Box sx={{overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Element style={{ position: 'absolute' }} name={item.id + SECTION.PERSONAL_INFO}></Element>
                        {reportData.collection?.taggable && (
                            <TagsViewer checkedTags={item.tags} styles={{ mr: 1.75 }} />
                        )}
                        {item.value ? (
                            reportData.displayHighlights ? (
                                <MultiMarkedText
                                    containerProps={{
                                        className: 'color-mh',
                                        variant: 's14w700',
                                        sx: multiMarking(
                                            item.id,
                                            SECTION.PERSONAL_INFO,
                                            'value',
                                            reportData.currentHighlightsLink,
                                        ),
                                    }}
                                    searchTextArr={item.keywords}
                                >
                                    {item.value}
                                </MultiMarkedText>
                            ) : (
                                <MarkedText
                                    containerProps={{
                                        className: 'color-mh',
                                        variant: 's14w700',
                                        sx: getObject('value'),
                                    }}
                                    searchText={reportData.find}
                                >
                                    {item.value}
                                </MarkedText>
                            )
                        ) : (
                            item.additionalInfo?.primaryInfoText &&
                            (reportData.displayHighlights ? (
                                <MultiMarkedText
                                    containerProps={{
                                        sx: multiMarking(
                                            item.id,
                                            SECTION.PERSONAL_INFO,
                                            'primaryInfoText',
                                            reportData.currentHighlightsLink,
                                        ),
                                    }}
                                    searchTextArr={item.keywords}
                                >
                                    {item.additionalInfo?.primaryInfoText}
                                </MultiMarkedText>
                            ) : (
                                <MarkedText
                                    containerProps={{
                                        sx: getObject('primaryInfoText'),
                                    }}
                                    searchText={reportData.find}
                                >
                                    {item.additionalInfo?.primaryInfoText}
                                </MarkedText>
                            ))
                        )}

                        {item.itemType ? (
                            item.itemType === 'isInitialSearchInput' ? (
                                <Tooltip
                                    placement="top"
                                    content={<Typography>{i18n.translate('Initial Search Input')}</Typography>}
                                >
                                    <SearchedItemsIcon className="fill-b" style={{ marginLeft: '12px' }} />
                                </Tooltip>
                            ) : (
                                <Tooltip
                                    placement="top"
                                    content={<Typography>{i18n.translate('Added Search')}</Typography>}
                                >
                                    <PossibleMatchesIcon className="fill-b" style={{ marginLeft: '12px' }} />
                                </Tooltip>
                            )
                        ) : null}
                    </Box>

                    {item.value &&
                        typeof item.additionalInfo?.primaryInfoText === 'string' &&
                        (reportData.displayHighlights ? (
                            <MultiMarkedText
                                containerProps={{
                                    className: 'color-bd',
                                    sx: multiMarking(
                                        item.id,
                                        SECTION.PERSONAL_INFO,
                                        'primaryInfoText',
                                        reportData.currentHighlightsLink,
                                    ),
                                }}
                                searchTextArr={item.keywords}
                            >
                                {item.additionalInfo?.primaryInfoText}
                            </MultiMarkedText>
                        ) : (
                            <MarkedText
                                containerProps={{
                                    className: 'color-bd',
                                    sx: getObject('primaryInfoText'),
                                }}
                                searchText={reportData.find}
                            >
                                {item.additionalInfo?.primaryInfoText}
                            </MarkedText>
                        ))}

                    {item.value &&
                        Array.isArray(item.additionalInfo?.primaryInfoText) &&
                        item.additionalInfo?.primaryInfoText.map((newRow, index) =>
                            reportData.displayHighlights ? (
                                <MultiMarkedText
                                    key={index}
                                    containerProps={{
                                        className: 'color-bd',
                                        sx: multiMarking(
                                            item.id,
                                            SECTION.PERSONAL_INFO,
                                            'primaryInfoText',
                                            reportData.currentHighlightsLink,
                                        ),
                                    }}
                                    searchTextArr={item.keywords}
                                >
                                    {newRow}
                                </MultiMarkedText>
                            ) : (
                                <MarkedText
                                    key={index}
                                    containerProps={{
                                        className: 'color-bd',
                                        sx: getObject('primaryInfoText'),
                                    }}
                                    searchText={reportData.find}
                                >
                                    {newRow}
                                </MarkedText>
                            ),
                        )}

                    {item.additionalInfo?.secondaryInfoText &&
                        (reportData.displayHighlights ? (
                            <MultiMarkedText
                                containerProps={{
                                    className: 'color-md',
                                    sx: multiMarking(
                                        item.id,
                                        SECTION.PERSONAL_INFO,
                                        'secondaryInfoText',
                                        reportData.currentHighlightsLink,
                                    ),
                                }}
                                searchTextArr={item.keywords}
                            >
                                {item.additionalInfo?.secondaryInfoText}
                            </MultiMarkedText>
                        ) : (
                            <MarkedText
                                containerProps={{
                                    className: 'color-md',
                                    sx: getObject('secondaryInfoText'),
                                }}
                                searchText={reportData.find}
                            >
                                {item.additionalInfo?.secondaryInfoText}
                            </MarkedText>
                        ))}
                </Box>

                <Box sx={{ display: 'flex', height: '25px', alignItems: 'center' }}>
                    {item.sources}
                    {reportData.collection?.taggable && (
                        <Box sx={{ ml: 1.5, minWidth: '24px' }}>
                            {(showTag || tagOpen) && (
                                <Popup
                                    onToggle={() => {
                                        setTagOpen(!tagOpen);
                                    }}
                                    actionElement={(handleOpen, anchorRef, open) => {
                                        return (
                                            <Box ref={anchorRef}>
                                                <IconButton
                                                    sx={{
                                                        'backgroundColor': tagOpen ? COLORS.MUTED_DOUBLE_HOVER : null,
                                                        '&:hover': { backgroundColor: COLORS.MUTED_DOUBLE_HOVER },
                                                    }}
                                                    disableRipple
                                                    onClick={handleOpen}
                                                >
                                                    <AddTagIcon fill={open ? COLORS.BRAND : COLORS.MUTED_DARK} />
                                                </IconButton>
                                            </Box>
                                        );
                                    }}
                                    contentElement={(handleClose) => (
                                        <Box>
                                            <TagsManager
                                                checkedTags={item.tags}
                                                section={SECTION.PERSONAL_INFO}
                                                elementId={item.id}
                                            />
                                        </Box>
                                    )}
                                />
                            )}
                        </Box>
                    )}
                    <Box sx={{ ml: 1.5, minWidth: '24px' }}>
                        {(showActions || actionsOpen) && (
                            <CustomActions
                                actions={item.actions || []}
                                actionTextStyle={{ mr: '8px', color: COLORS.BRAND_DARK }}
                                onToggleActions={() => setActionsOpen(!actionsOpen)}
                            />
                        )}
                    </Box>
                </Box>
            </Box>
            {item.additionalInfo?.associatedSites && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: '4px',
                            height: '24px',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <BreachedSiteIcon className="fill-md" style={{ marginRight: '12px' }} />
                            <Typography className="color-md">{i18n.translate('Associated Sites')}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography className="color-bd" sx={{ mr: '14px' }}>
                                {item.additionalInfo?.associatedSites.length} {i18n.translate('Sites')}
                            </Typography>
                            {isDropdownOpen ? (
                                <ArrowUpIcon
                                    className="fill-md"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setIsDropdownOpen(false)}
                                />
                            ) : (
                                <ArrowDownIcon
                                    className="fill-md"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setIsDropdownOpen(true)}
                                />
                            )}
                        </Box>
                    </Box>
                    <Collapse in={isDropdownOpen} timeout="auto" unmountOnExit>
                        <List component="div">
                            {item.additionalInfo.associatedSites.map((breachedObject, index) => (
                                <ListItem sx={{ pl: '32px', pr: 0 }} key={index}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            width: '100%',
                                        }}
                                    >
                                        <Tooltip
                                            placement="bottom"
                                            content={<Typography>{breachedObject.breachInfo}</Typography>}
                                            styles={{ maxWidth: '373px' }}
                                        >
                                            <Typography className="color-mh">{breachedObject.website}</Typography>
                                        </Tooltip>
                                        <Typography className="color-md">{breachedObject.breachDate}</Typography>
                                    </Box>
                                </ListItem>
                            ))}
                        </List>
                    </Collapse>
                </>
            )}
        </>
    );
};

export default CustomInfoCardRow;
